/**
 * Opening suggestions for the location assistant, drawn from the project in front of the user.
 *
 * Fixed examples were worse than useless here: they named "Tower B" and "Block A", which most
 * projects do not have, so clicking one asked the assistant about something that does not exist.
 * These name real locations, so every suggestion is a request that can actually succeed.
 */

import type { LocationTreeNode } from '../../../api/schedulingApis';
import { isFloorLocationType, isProjectLocationType } from './locationTypeRules';

/** Always offered: nobody thinks to ask for this in words, and it is the biggest thing here. */
const CLASSIFY = 'Build my locations from the schedule tasks';

/**
 * Offered when the project has no locations yet. A block first, because nothing else can be
 * placed until one exists, then floors under it, then a different top-level type.
 */
const EMPTY_PROJECT_SUGGESTIONS = [
  'Add a block called Tower A',
  'Create 2 blocks with 10 floors each',
  'Add a zone called Site Area',
];

/** Depth-first, so the first floor found is the one nearest the top of the tree. */
function findFirst(
  nodes: LocationTreeNode[],
  match: (node: LocationTreeNode) => boolean,
): LocationTreeNode | null {
  for (const node of nodes) {
    if (match(node)) return node;
    const hit = node.child?.length ? findFirst(node.child, match) : null;
    if (hit) return hit;
  }
  return null;
}

function named(node: LocationTreeNode | null | undefined): string | null {
  const name = node?.name?.trim();
  return name ? name : null;
}

export function buildChatSuggestions(tree: LocationTreeNode[]): string[] {
  const roots = tree ?? [];
  const projectRoot = roots.find((n) => isProjectLocationType(n.location_type));
  // Everything the project actually contains, whether or not it is wrapped in a project root.
  const topLevel = (projectRoot ? projectRoot.child ?? [] : roots).filter(
    (n) => !isProjectLocationType(n.location_type),
  );

  if (topLevel.length === 0) {
    return [CLASSIFY, ...EMPTY_PROJECT_SUGGESTIONS];
  }

  // Prefer a branch that already has something under it - asking what is inside an empty one
  // returns nothing, which is a poor first impression.
  const populated = topLevel.find((n) => (n.child ?? []).length > 0);
  const subject = named(populated) ?? named(topLevel[0]);
  const floor = named(findFirst(roots, (n) => isFloorLocationType(n.location_type)));

  const suggestions = [CLASSIFY];
  if (subject) {
    suggestions.push(`What is under ${subject}?`);
    suggestions.push(`Add 5 floors to ${subject}`);
  }
  // Something one level deeper, so the panel shows it can work below the top level too.
  if (floor) suggestions.push(`Add a room to ${floor}`);
  else suggestions.push('Add a block called Tower B');

  return suggestions;
}
